/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, LogService, RbacService, UserService, UtilsService } from '../../../../core/services';
import { EnduranceMenu } from '../lists/endurance.menu';
import { EnduranceRBACResourcesApiService, ViewConfigurationApiService } from '../http';
import { ErrorResponseModel, MenuConfigModel, MenuItemsModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, Subscriber } from 'rxjs';

@Injectable()
export class EnduranceResolverGuard {
    /**
     * Store a list of error to be passed to the dashboard.
     * @private
     */
    private errorList: any = {};

    constructor(
        private apiService: ApiService,
        private enduranceRBACResourcesApiService: EnduranceRBACResourcesApiService,
        private logService: LogService,
        private rbacService: RbacService,
        private userService: UserService,
        private utilsService: UtilsService,
        private viewConfigApiService: ViewConfigurationApiService
    ) {
    }

    /**
     * Can activate route.
     */
    public canActivate(): Observable<any> {
        return new Observable<any>((observe: Subscriber<any>): void => {
            /* eslint-disable max-len */
            const apiCalls = {
                routes: this.enduranceRBACResourcesApiService.getRoutesForCurrentUser().pipe(this.apiService.handleRequest(this.errorList, 'routes').bind(this)),
                configs: this.viewConfigApiService.getConfigs().pipe(this.apiService.handleRequest(this.errorList, 'configs').bind(this)),
            };
            /* eslint-enable max-len */

            forkJoin(apiCalls)
                .subscribe({
                    next: (data: any): void => {
                        this.rbacService.endpointsLookup = this.updateEndpoints(data);
                        this.rbacService.menu = this.updateViewConfigMenuItems(data, EnduranceMenu);

                        observe.next();
                        observe.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        if (error.apiStatus === 403) {
                            const timerId = setTimeout((): void => {
                                this.userService.logout();
                                clearTimeout(timerId);
                                window.location.reload();
                            }, 2000);
                        }

                        this.logService.error('EnduranceResolverGuard:canActivate', error.message);
                        observe.error(error);
                        observe.complete();
                    }
                });
        });
    }

    /**
     * Update the endpoints.
     * @param data
     * @private
     */
    private updateEndpoints(data: any) {
        if (!data.routes) {
            this.logService.error('EnduranceResolverGuard:updateEndpoints', 'Missing routes data', data);

            return;
        }

        return this.utilsService.sortArrayByProperty(data['routes'], 'path');
    }

    /**
     * Update view config menu items.
     * @param data
     * @param menu
     * @private
     */
    private updateViewConfigMenuItems(data: any, menu: MenuConfigModel): MenuConfigModel {
        if (!data.routes || !data.configs) {
            this.logService.error('EnduranceResolverGuard:updateViewConfigMenuItems', 'Missing routes or config data', data);
            const menuIndex: number = menu.menuItems.findIndex((menuItem: MenuItemsModel) => menuItem.location === './view-configuration');
            if (menuIndex !== -1) {
                menu.menuItems[menuIndex].hiddenForced = true;
            }

            return menu;
        } else {
            const viewConfigMenu: MenuItemsModel = menu?.menuItems.find(
                (item: MenuItemsModel): boolean => item.id === 'menu-view-configuration'
            );
            const routes: any = data.routes.find((item: any): boolean => item.refPath === '/configs');
            const show: boolean = routes.methods.GET;
            const menuChildren: any[] = [];

            const sortedConfigs: any = this.utilsService.sortArrayByProperty(data.configs, 'moduleName');
            for (const config of sortedConfigs) {
                menuChildren.push({
                    location: `./view-configuration/${config.module}`,
                    heading: this.utilsService.tryToMatchNameToLanguageFile(config.moduleName),
                    id: config.module,
                    hidden: !show
                });
            }

            viewConfigMenu.children = menuChildren;

            return menu;
        }
    }
}
